/* ==========================================================================
   Disable / Delete provider confirmations
   Shared by the Providers list and the provider details page (PORTAL-74).
   ========================================================================== */

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import type { ProviderResponse } from '@/api/types'

export interface ProviderConfirmModalProps {
  open: boolean
  provider: ProviderResponse | null
  loading: boolean
  onCancel: () => void
  onConfirm: () => void
}

export function DisableProviderModal({
  open,
  provider,
  loading,
  onCancel,
  onConfirm,
}: ProviderConfirmModalProps) {
  return (
    <Modal open={open} onClose={onCancel} title="Disable Provider" size="sm">
      <p className="text-gray-600 mb-6">
        Are you sure you want to disable{' '}
        <strong className="text-charcoal">{provider?.name || provider?.email}</strong>? They
        will no longer be able to log in or submit prescriptions.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
          className="sm:flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          loading={loading}
          className="sm:flex-1"
        >
          Disable Provider
        </Button>
      </div>
    </Modal>
  )
}

export function DeleteProviderModal({
  open,
  provider,
  loading,
  onCancel,
  onConfirm,
}: ProviderConfirmModalProps) {
  return (
    <Modal open={open} onClose={onCancel} title="Delete Provider" size="sm">
      <p className="text-gray-600 mb-6">
        Are you sure you want to permanently delete{' '}
        <strong className="text-charcoal">{provider?.name || provider?.email}</strong>? This
        action cannot be undone.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
          className="sm:flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          loading={loading}
          className="sm:flex-1"
        >
          Delete Provider
        </Button>
      </div>
    </Modal>
  )
}

/** Lost phone (PORTAL-187): turn off a provider's two-factor authentication. */
export function ResetProviderTwoFactorModal({
  open,
  provider,
  loading,
  onCancel,
  onConfirm,
}: ProviderConfirmModalProps) {
  return (
    <Modal open={open} onClose={onCancel} title="Reset Two-Factor" size="sm">
      <p className="text-gray-600 mb-6">
        Turn off two-factor authentication for{' '}
        <strong className="text-charcoal">{provider?.name || provider?.email}</strong>? Use
        this if they lost access to their authenticator app. They will sign in with just
        their password and can set it up again from their profile.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
          className="sm:flex-1"
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={onConfirm}
          loading={loading}
          className="sm:flex-1"
        >
          Reset Two-Factor
        </Button>
      </div>
    </Modal>
  )
}
